import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Send, 
  Edit3, 
  Trash2, 
  Reply, 
  MoreVertical, 
  Check, 
  X, 
  Loader2,
  User,
  FileImage,
  FileVideo,
  ChevronDown,
  XCircle,
  MessageSquare
} from 'lucide-react';
import { MessageWithUser, MessageService } from '../lib/messages';
import { supabase } from '../lib/supabase';

// Helper function to format message text for display (replaces creative IDs with names)
const formatMessageTextForDisplay = (text: string, mentions?: MessageWithUser['mentions']): string => {
  if (!mentions) {
    return text;
  }

  let formattedText = text;

  // Replace user mentions with names
  mentions.users?.forEach(user => {
    const mentionRegex = new RegExp(`@user:${user.id}`, 'g');
    formattedText = formattedText.replace(mentionRegex, `@${user.name || user.email.split('@')[0]}`);
  });

  // Replace creative mentions with names  
  mentions.creatives?.forEach(creative => {
    const mentionRegex = new RegExp(`@creative:${creative.id}`, 'g');
    formattedText = formattedText.replace(mentionRegex, `@${creative.name}`);
  });

  return formattedText;
};

// Message bubble component
interface MessageBubbleProps {
  message: MessageWithUser;
  currentUserId?: string;
  onEdit?: (messageId: string, newText: string) => void;
  onDelete?: (messageId: string) => void;
  onReply?: (message: MessageWithUser) => void;
  projectId?: string;
}

export function MessageBubble({ 
  message, 
  currentUserId, 
  onEdit, 
  onDelete, 
  onReply,
  projectId 
}: MessageBubbleProps) {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(message.messageText);
  const [showMenu, setShowMenu] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formattedReplyText, setFormattedReplyText] = useState<string>('');
  const menuRef = useRef<HTMLDivElement>(null);
  const messageContentRef = useRef<HTMLDivElement>(null);
  
  const isOwnMessage = message.userId === currentUserId;
  const isEdited = Boolean(message.editedAt);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format reply message text with mentions when component mounts
  useEffect(() => {
    const formatReplyMessage = async () => {
      if (message.replyToMessage && projectId) {
        // If reply already has mentions, use them
        if (message.replyToMessage.mentions) {
          setFormattedReplyText(formatMessageTextForDisplay(message.replyToMessage.messageText, message.replyToMessage.mentions));
          return;
        }

        // Otherwise, parse mentions from the message text
        const { userMentions, creativeMentions } = MessageService.parseMentions(message.replyToMessage.messageText);
        
        if (userMentions.length > 0 || creativeMentions.length > 0) {
          try {
            const [users, creatives] = await Promise.all([
              userMentions.length > 0 ? MessageService.getProjectMembers(projectId) : Promise.resolve([]),
              creativeMentions.length > 0 ? MessageService.getProjectCreatives(projectId) : Promise.resolve([])
            ]);
            
            const tempMentions = {
              users: users.filter(user => userMentions.includes(user.id)),
              creatives: creatives.filter(creative => creativeMentions.includes(creative.id))
            };
            
            const formatted = formatMessageTextForDisplay(message.replyToMessage.messageText, tempMentions);
            setFormattedReplyText(formatted);
          } catch (error) {
            console.error('Error formatting reply mentions:', error);
            setFormattedReplyText(message.replyToMessage.messageText);
          }
        } else {
          setFormattedReplyText(message.replyToMessage.messageText);
        }
      }
    };

    formatReplyMessage();
  }, [message.replyToMessage, projectId]);

  // Handle creative mention clicks using event delegation
  useEffect(() => {
    const handleCreativeMentionClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      if (target.classList.contains('mention-creative')) {
        event.preventDefault();
        event.stopPropagation();
        
        const creativeId = target.getAttribute('data-creative-id');
        
        if (creativeId && projectId) {
          // Navigate to project creatives tab and highlight the creative using React Router
          const url = `/dashboard/projects/${projectId}?tab=creatives&highlight=${creativeId}`;
          navigate(url);
        }
      }
    };

    const messageContent = messageContentRef.current;
    
    if (messageContent) {
      messageContent.addEventListener('click', handleCreativeMentionClick);

      return () => {
        messageContent.removeEventListener('click', handleCreativeMentionClick);
      };
    }
  }, [projectId, navigate, message.messageText]);

  // Format message text with mentions highlighted
  const formatMessageText = (text: string, mentions?: MessageWithUser['mentions']) => {
    if (!mentions) {
      return { __html: text };
    }

    let formattedText = text;

    // Replace user mentions with styled spans and tooltips
    mentions.users?.forEach(user => {
      const mentionRegex = new RegExp(`@user:${user.id}`, 'g');
      formattedText = formattedText.replace(
        mentionRegex,
        `<span class="mention-user" title="${user.name || user.email} (${user.email})" data-tooltip="User: ${user.name || user.email}&#10;Email: ${user.email}">@${user.name || user.email.split('@')[0]}</span>`
      );
    });

    // Replace creative mentions with clickable styled spans and tooltips
    if (mentions.creatives && mentions.creatives.length > 0) {
      mentions.creatives.forEach(creative => {
        const mentionRegex = new RegExp(`@creative:${creative.id}`, 'g');
        const replacementText = `<span class="mention-creative" 
              title="${creative.name} (${creative.type}) - Click to view" 
              data-tooltip="Creative: ${creative.name}&#10;Type: ${creative.type}&#10;Click to view in project"
              data-creative-id="${creative.id}">@${creative.name}</span>`;
        formattedText = formattedText.replace(mentionRegex, replacementText);
      });
    }

    return { __html: formattedText };
  };

  const handleSaveEdit = async () => {
    if (!editText.trim() || !onEdit) return;
    
    try {
      setSaving(true);
      await onEdit(message.id, editText.trim());
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to edit message:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditText(message.messageText);
    setIsEditing(false);
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 24 * 7) {
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  };

  const getUserInitials = (user: MessageWithUser['user']) => {
    if (user.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return user.email.slice(0, 2).toUpperCase();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`w-full px-3 py-2 sm:px-4 sm:py-3 hover:bg-gray-50 transition-colors group ${
        isOwnMessage ? '' : ''
      }`}
    >
      <div className={`flex gap-2 sm:gap-3 ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Avatar */}
        <div className="flex-shrink-0">
          {message.user.avatar ? (
            <img
              src={message.user.avatar}
              alt={message.user.name || message.user.email}
              className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center text-white text-xs font-medium">
              {getUserInitials(message.user)}
            </div>
          )}
        </div>

        {/* Message Content */}
        <div className={`flex-1 min-w-0 max-w-[75%] sm:max-w-md ${isOwnMessage ? 'mr-0 ml-auto' : 'ml-0 mr-auto'}`}>
          {/* Header with name and timestamp */}
          <div className={`flex items-center gap-2 mb-1 ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
            <span className="text-xs sm:text-sm font-medium text-gray-900 truncate">
              {message.user.name || message.user.email.split('@')[0]}
            </span>
            <span className="text-xs text-gray-500 flex-shrink-0">
              {formatTimestamp(message.createdAt)}
              {isEdited && <span className="ml-1">(edited)</span>}
            </span>
            
            {/* Action Menu */}
            {isOwnMessage && (
              <div className="relative flex-shrink-0" ref={menuRef}>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-all"
                >
                  <MoreVertical className="w-3 h-3 text-gray-500" />
                </button>
                
                <AnimatePresence>
                  {showMenu && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className={`absolute top-6 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10 min-w-[100px] ${
                        isOwnMessage ? 'right-0' : 'left-0'
                      }`}
                    >
                      <button
                        onClick={() => {
                          setIsEditing(true);
                          setShowMenu(false);
                        }}
                        className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Edit3 className="w-3 h-3" />
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          onDelete?.(message.id);
                          setShowMenu(false);
                        }}
                        className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 text-red-600 flex items-center gap-2"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Reply indicator */}
          {message.replyToMessage && (
            <div className={`mb-2 ${isOwnMessage ? 'text-right' : 'text-left'}`}>
              <div className={`inline-block bg-gray-100 border-l-3 border-purple-400 rounded px-2 py-1 text-xs text-gray-600 max-w-full ${
                isOwnMessage ? 'text-left' : 'text-left'
              }`}>
                <div className="font-medium truncate">{message.replyToMessage.user.name || message.replyToMessage.user.email}</div>
                <div className="truncate">{formattedReplyText || message.replyToMessage.messageText}</div>
              </div>
            </div>
          )}

          {/* Message bubble */}
          <div className={`${isOwnMessage ? 'text-right' : 'text-left'}`}>
            {isEditing ? (
              <div className="space-y-2">
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows={2}
                  autoFocus
                  disabled={saving}
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={handleCancelEdit}
                    disabled={saving}
                    className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={saving || !editText.trim()}
                    className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-3 h-3" />
                        Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              /* Message bubble with reply button as sibling */
              <div className={`flex items-center gap-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                {/* Reply button for other users (left side) */}
                {!isOwnMessage && !isEditing && onReply && (
                  <button
                    onClick={() => onReply(message)}
                    className="opacity-0 group-hover:opacity-100 text-xs text-gray-500 hover:text-gray-700 transition-all flex items-center gap-1 bg-white border border-gray-200 rounded-full px-2 py-1 shadow-sm hover:shadow-md z-10 flex-shrink-0"
                    title="Reply to this message"
                  >
                    <Reply className="w-3 h-3" />
                    <span className="hidden sm:inline text-xs">Reply</span>
                  </button>
                )}
                
                {/* Message bubble */}
                <div
                  className={`inline-block px-3 py-2 rounded-2xl text-sm message-content break-words ${
                    isOwnMessage
                      ? 'bg-gradient-to-br from-purple-600 to-blue-600 text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                  ref={messageContentRef}
                >
                  <div 
                    dangerouslySetInnerHTML={formatMessageText(message.messageText, message.mentions)} 
                  />
                </div>
                
                {/* Reply button for own messages (right side) */}
                {isOwnMessage && !isEditing && onReply && (
                  <button
                    onClick={() => onReply(message)}
                    className="opacity-0 group-hover:opacity-100 text-xs text-gray-500 hover:text-gray-700 transition-all flex items-center gap-1 bg-white border border-gray-200 rounded-full px-2 py-1 shadow-sm hover:shadow-md z-10 flex-shrink-0"
                    title="Reply to this message"
                  >
                    <Reply className="w-3 h-3" />
                    <span className="hidden sm:inline text-xs">Reply</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Message list component
interface MessageListProps {
  messages: MessageWithUser[];
  currentUserId?: string;
  loading?: boolean;
  error?: string | null;
  onEdit?: (messageId: string, newText: string) => void;
  onDelete?: (messageId: string) => void;
  onReply?: (message: MessageWithUser) => void;
  projectId?: string;
}

export function MessageList({ 
  messages, 
  currentUserId, 
  loading, 
  error, 
  onEdit, 
  onDelete, 
  onReply,
  projectId 
}: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 min-h-[200px]">
        <div className="flex items-center space-x-3 text-gray-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading messages...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        <div className="flex items-center space-x-2">
          <XCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="text-center py-8 text-gray-600">
        <MessageSquare className="w-8 h-8 mx-auto mb-2" />
        <p>No messages yet. Start the conversation!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-h-96 overflow-y-auto scrollbar-thin p-4 bg-gray-50 rounded-lg">
      <AnimatePresence>
        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            currentUserId={currentUserId}
            onEdit={onEdit}
            onDelete={onDelete}
            onReply={onReply}
            projectId={projectId}
          />
        ))}
      </AnimatePresence>
      <div ref={messagesEndRef} />
    </div>
  );
}

// Mention suggestion interface
interface SuggestionItem {
  id: string;
  name: string;
  type: 'user' | 'creative';
  email?: string;
  creativeType?: string;
}

// Enhanced message input component with mentions
interface MessageInputProps {
  onSend: (message: string) => void;
  placeholder?: string;
  disabled?: boolean;
  sending?: boolean;
  replyTo?: MessageWithUser | null;
  onCancelReply?: () => void;
  projectId?: string;
}

export function MessageInput({ 
  onSend, 
  placeholder = "Type a message...", 
  disabled = false, 
  sending = false,
  replyTo,
  onCancelReply,
  projectId 
}: MessageInputProps) {
  const [message, setMessage] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Track creative name to ID mapping for message sending
  const [creativeNameToIdMap, setCreativeNameToIdMap] = useState<Map<string, string>>(new Map());
  const [formattedReplyPreview, setFormattedReplyPreview] = useState<string>('');

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  // Format reply preview text with mentions
  useEffect(() => {
    const formatReplyPreview = async () => {
      if (replyTo && projectId) {
        // If reply already has mentions, use them
        if (replyTo.mentions) {
          setFormattedReplyPreview(formatMessageTextForDisplay(replyTo.messageText, replyTo.mentions));
          return;
        }

        // Otherwise, parse mentions from the message text
        const { userMentions, creativeMentions } = MessageService.parseMentions(replyTo.messageText);
        
        if (userMentions.length > 0 || creativeMentions.length > 0) {
          try {
            const [users, creatives] = await Promise.all([
              userMentions.length > 0 ? MessageService.getProjectMembers(projectId) : Promise.resolve([]),
              creativeMentions.length > 0 ? MessageService.getProjectCreatives(projectId) : Promise.resolve([])
            ]);
            
            const tempMentions = {
              users: users.filter(user => userMentions.includes(user.id)),
              creatives: creatives.filter(creative => creativeMentions.includes(creative.id))
            };
            
            const formatted = formatMessageTextForDisplay(replyTo.messageText, tempMentions);
            setFormattedReplyPreview(formatted);
          } catch (error) {
            console.error('Error formatting reply preview mentions:', error);
            setFormattedReplyPreview(replyTo.messageText);
          }
        } else {
          setFormattedReplyPreview(replyTo.messageText);
        }
      }
    };

    formatReplyPreview();
  }, [replyTo, projectId]);

  // Fetch suggestions from the backend
  const fetchSuggestions = async (query: string) => {
    if (!projectId || query.length < 1) {
      setSuggestions([]);
      return;
    }

    try {
      setLoadingSuggestions(true);
      const allSuggestions: SuggestionItem[] = [];
      
      // Fetch project team members
      const { data: members, error: memberError } = await supabase
        .from('team_members')
        .select(`
          users!team_members_user_id_fkey(id, name, email)
        `)
        .eq('project_id', projectId)
        .eq('invitation_status', 'accepted');

      if (!memberError && members) {
        const userSuggestions: SuggestionItem[] = members
          .map(member => member.users as any)
          .filter(user => user && (
            user.name?.toLowerCase().includes(query.toLowerCase()) ||
            user.email?.toLowerCase().includes(query.toLowerCase())
          ))
          .map(user => ({
            id: user.id,
            name: user.name || user.email.split('@')[0],
            email: user.email,
            type: 'user' as const
          }));
        
        allSuggestions.push(...userSuggestions);
      }
      
      // Fetch project creatives
      const { data: creatives, error: creativesError } = await supabase
        .from('creatives')
        .select('id, name, type')
        .eq('project_id', projectId);

      if (!creativesError && creatives) {
        const creativeSuggestions: SuggestionItem[] = creatives
          .filter(creative => creative.name?.toLowerCase().includes(query.toLowerCase()))
          .map(creative => ({
            id: creative.id,
            name: creative.name,
            type: 'creative' as const,
            creativeType: creative.type
          }));
        
        allSuggestions.push(...creativeSuggestions);
      }
      
      // Sort: users first, then creatives, both alphabetically
      allSuggestions.sort((a, b) => {
        if (a.type !== b.type) {
          return a.type === 'user' ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      });
      
      const limitedSuggestions = allSuggestions.slice(0, 10); // Limit to 10 suggestions
      setSuggestions(limitedSuggestions);
      
      // Update creative name mapping for message conversion
      updateCreativeMapping(limitedSuggestions);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Update the creative mapping when suggestions are fetched
  const updateCreativeMapping = (suggestions: SuggestionItem[]) => {
    const newMap = new Map(creativeNameToIdMap);
    suggestions.forEach(suggestion => {
      if (suggestion.type === 'creative') {
        newMap.set(suggestion.name, suggestion.id);
      }
    });
    setCreativeNameToIdMap(newMap);
  };

  // Convert creative name mentions back to ID format for backend processing
  const convertMentionsForSending = (text: string): string => {
    let convertedText = text;
    
    // Convert @CreativeName back to @creative:id format
    creativeNameToIdMap.forEach((id, name) => {
      const namePattern = new RegExp(`@${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$)`, 'g');
      convertedText = convertedText.replace(namePattern, `@creative:${id}`);
    });
    
    return convertedText;
  };

  // Handle text change and mention detection
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newMessage = e.target.value;
    const cursorPosition = e.target.selectionStart;
    
    setMessage(newMessage);

    // Detect mentions - now triggers after 1+ characters
    const textBeforeCursor = newMessage.substring(0, cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@([^@\s]{0,})$/);
    
    if (mentionMatch) {
      const [fullMatch, query] = mentionMatch;
      const start = cursorPosition - fullMatch.length;
      
      setMentionStart(start);
      setMentionQuery(query);
      
      if (query.length >= 1) {
        setShowSuggestions(true);
        setSelectedSuggestionIndex(0);
        fetchSuggestions(query);
      } else {
        setShowSuggestions(false);
        setSuggestions([]);
      }
    } else {
      setShowSuggestions(false);
      setMentionStart(null);
      setMentionQuery('');
      setSuggestions([]);
    }
  };

  // Handle suggestion selection
  const selectSuggestion = (suggestion: SuggestionItem) => {
    if (mentionStart === null || !textareaRef.current) return;

    const beforeMention = message.substring(0, mentionStart);
    const afterMention = message.substring(textareaRef.current.selectionStart);
    
    // For creatives, show the name directly; for users, keep the ID format for backend processing
    const mentionText = suggestion.type === 'creative' 
      ? `@${suggestion.name}` 
      : `@${suggestion.type}:${suggestion.id}`;
    
    const newMessage = beforeMention + mentionText + ' ' + afterMention;
    setMessage(newMessage);
    
    // Add this creative to our mapping if it's not already there
    if (suggestion.type === 'creative') {
      setCreativeNameToIdMap(prev => new Map(prev).set(suggestion.name, suggestion.id));
    }
    
    // Reset mention state
    setShowSuggestions(false);
    setMentionStart(null);
    setMentionQuery('');
    setSuggestions([]);
    
    // Focus back to textarea and position cursor
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPosition = beforeMention.length + mentionText.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showSuggestions && suggestions.length > 0) {
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          setSelectedSuggestionIndex(prev => 
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedSuggestionIndex(prev => 
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (suggestions[selectedSuggestionIndex]) {
            selectSuggestion(suggestions[selectedSuggestionIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setShowSuggestions(false);
          setSuggestions([]);
          break;
        default:
          break;
      }
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    if (message.trim() && !disabled && !sending) {
      onSend(convertMentionsForSending(message.trim()));
      setMessage('');
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full">
      {/* Reply indicator */}
      {replyTo && (
        <div className="mb-3 p-3 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg mx-3 sm:mx-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-sm">
                <span className="text-gray-600">Replying to </span>
                <span className="font-medium text-blue-700">{replyTo.user.name || replyTo.user.email.split('@')[0]}</span>
              </div>
              <div className="text-sm text-gray-600 truncate mt-1">{formattedReplyPreview || replyTo.messageText}</div>
            </div>
            <button
              onClick={onCancelReply}
              className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 transition-colors"
              title="Cancel reply"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="flex items-end space-x-3 p-4 border-t border-gray-200 bg-white">
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || sending}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent max-h-32 disabled:bg-gray-50 disabled:text-gray-500"
            rows={1}
          />
          
          {/* Mentions help text */}
          <div className="absolute -top-12 left-0 right-0 bg-gray-800 text-white text-xs px-3 py-2 rounded-lg opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
            Type @ followed by 1+ characters to mention team members or creatives
          </div>

          {/* Suggestions dropdown */}
          {showSuggestions && (
            <div 
              ref={suggestionsRef}
              className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50"
            >
              {loadingSuggestions ? (
                <div className="p-3 text-center text-gray-600 flex items-center justify-center space-x-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading suggestions...</span>
                </div>
              ) : suggestions.length > 0 ? (
                <div className="py-1">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={`${suggestion.type}-${suggestion.id}`}
                      onClick={() => selectSuggestion(suggestion)}
                      className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors ${
                        index === selectedSuggestionIndex ? 'bg-purple-50 border-l-4 border-purple-500' : ''
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        suggestion.type === 'user' ? 'bg-blue-100' : 'bg-green-100'
                      }`}>
                        {suggestion.type === 'user' ? (
                          <User className={`w-4 h-4 ${suggestion.type === 'user' ? 'text-blue-600' : 'text-green-600'}`} />
                        ) : (
                          suggestion.creativeType === 'video' ? (
                            <FileVideo className="w-4 h-4 text-green-600" />
                          ) : (
                            <FileImage className="w-4 h-4 text-green-600" />
                          )
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{suggestion.name}</div>
                        <div className="text-sm text-gray-600 truncate">
                          {suggestion.type === 'user' 
                            ? suggestion.email 
                            : `${suggestion.creativeType} creative`
                          }
                        </div>
                      </div>
                      <div className={`text-xs px-2 py-1 rounded-full ${
                        suggestion.type === 'user' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {suggestion.type}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-3 text-center text-gray-600">
                  <div className="text-sm">No matches found</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {mentionQuery.length < 1 
                      ? `Type ${1 - mentionQuery.length} more character` 
                      : `No users or creatives match "${mentionQuery}"`
                    }
                  </div>
                </div>
              )}
              
              {/* Instructions */}
              <div className="border-t border-gray-100 p-2 bg-gray-50">
                <div className="text-xs text-gray-600 flex items-center justify-between">
                  <span>↑↓ Navigate • Enter Select • Esc Cancel</span>
                  <span className="text-gray-400">@{mentionQuery}</span>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <button
          onClick={handleSend}
          disabled={disabled || sending || !message.trim()}
          className="p-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          title="Send message"
        >
          {sending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </div>
    </div>
  );
}

// Add custom CSS for mentions
const mentionStyles = `
  .mention-user {
    background-color: #bfdbfe;
    color: #1e40af;
    padding: 1px 4px;
    border-radius: 4px;
    font-weight: 500;
  }
  
  .mention-creative {
    background-color: #bbf7d0;
    color: #065f46;
    padding: 1px 4px;
    border-radius: 4px;
    font-weight: 500;
  }
  
  .message-content {
    line-height: 1.4;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = mentionStyles;
  document.head.appendChild(styleSheet);
} 